import * as THREE from 'three';

/**
 * Camera.js - view system.
 *
 * The visitor stands in the center of her universe. Drag to rotate the view
 * (look around). Scroll/WASD to drift through space. Damping for smoothness.
 */
export class Camera {
  constructor() {
    this.three = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      10000,
    );
    this.three.position.set(0, 0, 0);

    // Look direction in spherical coordinates (radians)
    this.theta = 0;             // azimuth
    this.phi = Math.PI / 2;     // polar (PI/2 = horizon)
    this.targetTheta = this.theta;
    this.targetPhi = this.phi;

    // Drag state
    this.isDragging = false;
    this.dragStart = { x: 0, y: 0 };
    this.dragStartTheta = 0;
    this.dragStartPhi = 0;

    // Lock - when true, ignore pointer/touch/wheel input
    this.locked = false;

    // Drift state (forward/backward through space)
    this.driftSpeed = 0;
    this.targetDriftSpeed = 0;

    this.bindEvents();
  }

  bindEvents() {
    // Wait for canvas to exist before binding
    requestAnimationFrame(() => {
      const canvas = document.querySelector('canvas');
      if (!canvas) return;

      // Mouse drag
      canvas.addEventListener('mousedown', this.onPointerDown);
      window.addEventListener('mousemove', this.onPointerMove);
      window.addEventListener('mouseup', this.onPointerUp);

      // Touch
      canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
      window.addEventListener('touchmove', this.onTouchMove, { passive: false });
      window.addEventListener('touchend', this.onPointerUp);

      // Scroll to drift
      canvas.addEventListener('wheel', this.onWheel, { passive: false });
    });
  }

  onPointerDown = (e) => {
    if (this.locked) return;
    this.isDragging = true;
    this.dragStart.x = e.clientX;
    this.dragStart.y = e.clientY;
    this.dragStartTheta = this.targetTheta;
    this.dragStartPhi = this.targetPhi;
  };

  onPointerMove = (e) => {
    if (!this.isDragging) return;
    const dx = e.clientX - this.dragStart.x;
    const dy = e.clientY - this.dragStart.y;
    this.targetTheta = this.dragStartTheta - dx * 0.004;
    this.targetPhi = clamp(this.dragStartPhi - dy * 0.004, 0.15, Math.PI - 0.15);
  };

  onTouchStart = (e) => {
    if (this.locked) return;
    if (e.touches.length !== 1) return;
    e.preventDefault();
    this.isDragging = true;
    this.dragStart.x = e.touches[0].clientX;
    this.dragStart.y = e.touches[0].clientY;
    this.dragStartTheta = this.targetTheta;
    this.dragStartPhi = this.targetPhi;
  };

  onTouchMove = (e) => {
    if (!this.isDragging || e.touches.length !== 1) return;
    e.preventDefault();
    const dx = e.touches[0].clientX - this.dragStart.x;
    const dy = e.touches[0].clientY - this.dragStart.y;
    this.targetTheta = this.dragStartTheta - dx * 0.004;
    this.targetPhi = clamp(this.dragStartPhi - dy * 0.004, 0.15, Math.PI - 0.15);
  };

  onPointerUp = () => {
    this.isDragging = false;
  };

  onWheel = (e) => {
    if (this.locked) return;
    e.preventDefault();
    // Inverted because most users expect wheel-up = forward
    this.targetDriftSpeed = -e.deltaY * 0.05;
  };

  update(dt) {
    // When locked (opening/finale), the controlling sequence sets the
    // camera position and look direction directly each frame. Skip our
    // own input + lookAt logic entirely.
    if (this.locked) return;

    // Damp angles toward targets
    this.theta = lerp(this.theta, this.targetTheta, 0.12);
    this.phi = lerp(this.phi, this.targetPhi, 0.12);

    // Damp drift speed (decays to zero)
    this.driftSpeed = lerp(this.driftSpeed, this.targetDriftSpeed, 0.15);
    this.targetDriftSpeed *= 0.85;
    if (Math.abs(this.targetDriftSpeed) < 0.001) this.targetDriftSpeed = 0;

    // Compute look direction
    const dir = new THREE.Vector3(
      Math.sin(this.phi) * Math.cos(this.theta),
      Math.cos(this.phi),
      Math.sin(this.phi) * Math.sin(this.theta),
    );

    // Drift the camera position along the look direction
    if (Math.abs(this.driftSpeed) > 0.001) {
      this.three.position.addScaledVector(dir, this.driftSpeed * dt * 8);
    }

    // Clamp camera position so it can't drift too far from origin.
    // The celestial sphere is at radius 800 - if the camera gets too close
    // to a sprite on that sphere, the sprite blows up to fill the screen.
    // Clamping to MAX_DRIFT keeps every sprite at a reasonable apparent size.
    const MAX_DRIFT = 100;
    if (this.three.position.length() > MAX_DRIFT) {
      this.three.position.normalize().multiplyScalar(MAX_DRIFT);
      this.driftSpeed = 0;
      this.targetDriftSpeed = 0;
    }

    // Aim the camera
    const target = this.three.position.clone().add(dir);
    this.three.lookAt(target);
  }

  onResize(w, h) {
    this.three.aspect = w / h;
    this.three.updateProjectionMatrix();
  }

  /**
   * After an external sequence has been driving the camera (opening,
   * finale), call this to sync our internal theta/phi state to the
   * camera's current orientation so subsequent drag input is smooth.
   */
  resetToCurrentPose() {
    // Compute the look direction from the camera's actual world matrix
    const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(this.three.quaternion);
    // Convert to spherical (theta = azimuth, phi = polar from +Y)
    this.phi = Math.acos(Math.max(-1, Math.min(1, dir.y)));
    this.theta = Math.atan2(dir.z, dir.x);
    this.targetPhi = this.phi;
    this.targetTheta = this.theta;
  }
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}
